import React, { useEffect, useState } from "react";
import { formatPrice } from "../Utility/Utility";
import pointIcon from "../../Assets/point_icon.svg";

// Same breakpoint as the thank-you page: the fee breakdown starts open on
// mobile and closed on desktop.
const MOBILE_QUERY = "(max-width: 700px)";

const CartSummary = ({ summary, translation, earning, earnStyle }) => {
  const [isFeeExpanded, setIsFeeExpanded] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

  useEffect(() => {
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const syncWithScreen = ({ matches }) => setIsFeeExpanded(matches);
    mobileQuery.addEventListener("change", syncWithScreen);
    return () => mobileQuery.removeEventListener("change", syncWithScreen);
  }, []);

  // As on the thank-you page: no fee row at all when nothing is charged.
  const hasConvenienceFee = summary.convenienceFee > 0;
  const voucherLabel =
    summary.totalQuantity === 1 ? translation?.cartGiftVoucher : translation?.cartGiftVouchers;
  const feeLabel = (fee) => `${fee.productName} · ${fee.feePercent}% ${translation?.cartPlusGst ?? ""}`.trim();

  return (
    <div className="cart-summary">
      <h2 className="cart-summary__title">{translation?.orderSummary}</h2>
      <div className={`cart-summary__card ${earning ? "cart-summary__card--with-earn" : ""}`}>
        <div className="cart-summary__row">
          <span className="cart-summary__label">
            {summary.totalQuantity} {voucherLabel}
          </span>
          <span className="cart-summary__value">₹{formatPrice(summary.totalDenomination)}</span>
        </div>

        {/* Hidden when no card in the cart has a discount. */}
        {summary.totalDiscount > 0 && (
          <div className="cart-summary__row">
            <span className="cart-summary__label">{translation?.cartTotalDiscount}</span>
            <span className="cart-summary__value">₹{formatPrice(summary.totalDiscount)}</span>
          </div>
        )}

        {/* One "Convenience Fee" row with a toggle, then one line per product. */}
        {hasConvenienceFee && (
          <div className="cart-summary__fee">
            <div className="cart-summary__row">
              <button
                type="button"
                className="cart-summary__label cart-summary__fee-toggle"
                onClick={() => setIsFeeExpanded((expanded) => !expanded)}
                aria-expanded={isFeeExpanded}
              >
                {translation?.cartConvenienceFee}
                <svg
                  className={`cart-summary__fee-icon ${isFeeExpanded ? "is-expanded" : ""}`}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M6.23 20.23 8 22l10-10L8 2 6.23 3.77 14.46 12z" />
                </svg>
              </button>
              <span className="cart-summary__value">₹{formatPrice(summary.convenienceFee)}</span>
            </div>

            {isFeeExpanded &&
              summary.feeBreakdown.map((fee) => (
                <div className="cart-summary__row cart-summary__row--sub" key={fee.key}>
                  <span>{feeLabel(fee)}</span>
                  <span>₹{formatPrice(fee.amount)}</span>
                </div>
              ))}
          </div>
        )}

        <div className="cart-summary__row cart-summary__row--total">
          <span className="cart-summary__label">{translation?.totalAmount}</span>
          <span className="cart-summary__value">₹{formatPrice(summary.totalAmount)}</span>
        </div>
      </div>

      {/* Same wording as the product page: "You'll earn <coin> N worth ₹X",
          or "Earn N Pts" for wallet-journey clients without the earn label. */}
      {earning && (
        <div className="cart-summary__earn">
          {earnStyle === "points" ? (
            <>
              {translation?.earn}{" "}
              <b className="cart-summary__earn-points">{earning.points.toLocaleString("en-IN")}</b>{" "}
              {translation?.pts}
            </>
          ) : (
            <>
              {translation?.checkoutWillEarn}{" "}
              <img src={pointIcon} alt="" aria-hidden="true" />{" "}
              <b className="cart-summary__earn-points">{earning.points.toLocaleString("en-IN")}</b>
              {earning.worth > 0 && (
                <> {translation?.checkoutWorth} ₹{formatPrice(earning.worth)}</>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CartSummary;
